import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import {
  clearAnalyticsChoice,
  isAnalyticsAccountSuppressed,
  readAnalyticsChoice,
  setAnalyticsAccount,
  setAnalyticsAccountSuppressed,
  writeAnalyticsChoice,
} from '@/libs/analytics-choice';
import api from '@/libs/axiosInstance';
import { productAnalytics, resetProductAnalytics } from '@/libs/product-analytics';
import type { ApiResponse, ProductAnalyticsPreferences } from '@fluenceur/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

type Preferences = ProductAnalyticsPreferences & { choice: 'granted' | 'denied' | null };
interface AnalyticsContextValue {
  enabled: boolean;
  choice: boolean | null;
  available: boolean;
  busy: boolean;
  change: (enabled: boolean) => void;
}
const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

export function ProductAnalyticsProvider({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [localChoice, setLocalChoice] = useState(readAnalyticsChoice);
  const [withdrawnScope, setWithdrawnScope] = useState<string | null>(null);
  const userId = user?.id ?? null;
  const currentUserId = useRef(userId);
  currentUserId.current = userId;
  const key = ['productAnalyticsPreferences', userId];
  const locallySuppressed =
    !!userId && (withdrawnScope === userId || isAnalyticsAccountSuppressed(userId));
  const preferences = useQuery({
    queryKey: key,
    enabled: !!userId && !isLoading,
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Preferences>>('/dashboard/analytics/preferences');
      if (!data.data) throw new Error('Missing analytics preferences');
      return data.data;
    },
    staleTime: 60_000,
    retry: false,
  });
  const enabled =
    !isLoading &&
    !locallySuppressed &&
    (userId
      ? preferences.data?.enabled === true && preferences.data.available
      : localChoice === 'granted');

  // Layout effects reset before a destination route can capture in its effects.
  useLayoutEffect(() => {
    setAnalyticsAccount(userId);
    resetProductAnalytics();
    return () => {
      setAnalyticsAccount(null);
      resetProductAnalytics();
    };
  }, [userId]);
  useLayoutEffect(() => {
    productAnalytics.setEnabled(enabled);
  }, [enabled, userId]);
  useEffect(() => {
    if (userId) {
      clearAnalyticsChoice();
      setLocalChoice(null);
    } else setLocalChoice(readAnalyticsChoice());
  }, [userId]);

  const mutation = useMutation<
    Preferences,
    AxiosError<ApiResponse>,
    boolean,
    { userId: string | null }
  >({
    mutationFn: async (next) => {
      const { data } = await api.put<ApiResponse<Preferences>>('/dashboard/analytics/preferences', {
        enabled: next,
      });
      if (!data.data) throw new Error('Missing analytics preferences');
      return data.data;
    },
    onMutate: () => ({ userId }),
    onSuccess: (data, _next, target) => {
      if (target?.userId !== currentUserId.current) return;
      if (_next) {
        if (userId) setAnalyticsAccountSuppressed(userId, false);
        setWithdrawnScope(null);
      }
      queryClient.setQueryData(key, data);
      toaster.success({ title: t('analytics.saved') });
    },
    onError: () => toaster.error({ title: t('analytics.saveError') }),
  });
  const choice = locallySuppressed
    ? false
    : userId
      ? preferences.data?.choice === 'granted'
        ? true
        : preferences.data?.choice === 'denied'
          ? false
          : null
      : localChoice === 'granted'
        ? true
        : localChoice === 'denied'
          ? false
          : null;
  return (
    <AnalyticsContext.Provider
      value={{
        enabled,
        choice,
        available: userId ? preferences.data?.available === true : true,
        busy: isLoading || preferences.isFetching || mutation.isPending,
        change: (next) => {
          // Fence capture and server facts before TanStack schedules the mutation.
          if (!next) {
            resetProductAnalytics();
            if (userId) {
              setAnalyticsAccountSuppressed(userId, true);
              setWithdrawnScope(userId);
            } else clearAnalyticsChoice();
          }
          if (userId) mutation.mutate(next);
          else {
            writeAnalyticsChoice(next);
            setLocalChoice(next ? 'granted' : 'denied');
          }
        },
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useProductAnalytics() {
  const context = useContext(AnalyticsContext);
  if (!context) throw new Error('ProductAnalyticsProvider is required');
  return context;
}
